import React, { useState, useEffect, useRef } from "react";
import { connectWisdomWallet } from "../chain";
import "./HeadMenu.css";

const WisdomAcademyStart = (props) => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const menuRef = useRef(null);
    const [quizAnswers, setQuizAnswers] = useState({ q1: "", q2: "", q3: "" });
    const [quizResult, setQuizResult] = useState(null);

    const toggleDropdown = (menu) => {
        setOpenDropdown(openDropdown === menu ? null : menu);
    };

    const loginWithWallet = async () => {
        try {
            const address = await connectWisdomWallet();
            if (props.onConnect) {
                props.onConnect(address);
            }
        } catch (err) {
            const msg = (err && err.message) || "Failed to connect wallet";
            alert(msg);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenDropdown(null);
                setMobileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleQuizSubmit = () => {
        const { q1, q2, q3 } = quizAnswers;
        if (q1 && q2 && q3) {
            let result = "";
            if (q1 === "visual" && q2 === "fast" && q3 === "very") {
                result = "You’re a Visual Speed Learner! Our AI will deliver quick, image-rich lessons with real-time feedback.";
            } else if (q1 === "hands-on" && q2 === "moderate" && q3 === "somewhat") {
                result = "You’re a Practical Balancer! Our AI will craft interactive tasks at a steady pace with periodic check-ins.";
            } else if (q1 === "text" && q2 === "slow" && q3 === "not") {
                result = "You’re a Deep Reader! Our AI will provide detailed text-based content for self-paced mastery.";
            } else if (q1 === "visual" && q2 === "moderate" && q3 === "very") {
                result = "You’re a Visual Feedback Seeker! Our AI will blend visuals with steady progress and constant updates.";
            } else {
                result = "You’re a Flexible Explorer! Our AI will adapt a mix of styles to suit your unique learning journey.";
            }
            setQuizResult(result);
        } else {
            alert("Please answer all questions!");
        }
    };

    const closeMenu = () => {
        setOpenDropdown(null);
        setMobileOpen(false);
    };

    return (
        <div className="landing">
            <header className={`headmenu${mobileOpen ? " is-open" : ""}`} ref={menuRef} aria-label="Main Navigation">
                <a className="brand" href="/" onClick={closeMenu}>
                    <img src="/pictures/wisdom-mark.svg" alt="" />
                    <span className="brand-copy">
                        <strong>Wisdom Academy</strong>
                        <span>AI Learning</span>
                    </span>
                </a>

                <button
                    type="button"
                    className="menu-toggle"
                    aria-label="Menu"
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((open) => !open)}
                >
                    ☰
                </button>

                <nav className="nav-container">
                    <ul className="menu-container">
                        <li className="dropdown">
                            <a href="#about" onClick={closeMenu}>About</a>
                        </li>
                        <li className="dropdown">
                            <button
                                type="button"
                                onClick={() => toggleDropdown("features")}
                                aria-expanded={openDropdown === "features"}
                                aria-haspopup="true"
                            >
                                Features
                            </button>
                            {openDropdown === "features" && (
                                <ul className="dropdown-menu" role="menu">
                                    <li><a href="#features" role="menuitem" onClick={closeMenu}>Learning Plans</a></li>
                                    <li><a href="#features" role="menuitem" onClick={closeMenu}>Assessments</a></li>
                                    <li><a href="#features" role="menuitem" onClick={closeMenu}>Rewards</a></li>
                                </ul>
                            )}
                        </li>
                        <li className="dropdown">
                            <a href="#approach" onClick={closeMenu}>Our AI</a>
                        </li>
                        <li className="dropdown">
                            <a href="#contact" onClick={closeMenu}>Contact</a>
                        </li>
                    </ul>
                </nav>

                <div className="menu-right">
                    <button type="button" className="login-btn" onClick={loginWithWallet}>
                        Login with MetaMask
                    </button>
                </div>
            </header>

            <section id="hero" className="hero">
                <img
                    className="hero-media"
                    src="/pictures/wisdom-hero-library.jpg"
                    alt=""
                    width="1280"
                    height="720"
                />
                <div className="container">
                    <p className="hero-kicker">Wisdom Academy AI</p>
                    <h1>Unleash Your Potential with AI &amp; Blockchain</h1>
                    <p>Embark on a learning journey tailored just for you—guided by cutting-edge AI, secured by blockchain, and rewarded with tokens you can own.</p>
                    <div className="hero-actions">
                        <button type="button" className="cta" onClick={loginWithWallet}>
                            Login with MetaMask
                        </button>
                        <a className="cta-secondary" href="#about">Explore the academy</a>
                    </div>
                </div>
            </section>

            <section id="about" className="wisdomacademy">
                <div className="container">
                    <h2>About Wisdom Academy</h2>
                    <p className="section-lead">
                        An AI-assisted learning platform with credentials and rewards recorded on the Wisdom chain (network 2307).
                    </p>
                    <div className="about">
                        <article className="about-tile">
                            <h3>AI that adapts to you</h3>
                            <p><b>Say goodbye to one-size-fits-all</b>. Our AI crafts a <b>learning adventure just for you</b>, matching your rhythm—whether you’re racing ahead or savoring every step.</p>
                        </article>
                        <article className="about-tile">
                            <h3>Credentials you can keep</h3>
                            <p>Your victories deserve more than a pat on the back. With <b>blockchain’s unbreakable seal</b>, your achievements become <b>timeless proof of your brilliance</b>. Show the world what you’re made of.</p>
                        </article>
                        <article className="about-tile">
                            <h3>Rewards that follow progress</h3>
                            <p>Why just learn when you can <b>earn as you grow</b>? Every breakthrough unlocks <b>WSD Tokens</b>—yours to spend, trade, or celebrate. Feel the thrill of progress that pays off.</p>
                        </article>
                        <article className="about-tile">
                            <h3>Teach, and shape the catalogue</h3>
                            <p>Your wisdom is power. <b>Shape your own courses</b>, inspire others, and earn rewards—all secured on the blockchain. Step into a community where <b>your knowledge shapes the future</b>.</p>
                        </article>
                    </div>
                </div>
            </section>

            <section id="features" className="features">
                <div className="container">
                    <h2>Your Path to Mastery Starts Here</h2>
                    <p className="section-lead">Courses, assessments, mentors, and a marketplace for WSD — one wallet session on the Wisdom chain.</p>
                    <ul className="feature-grid">
                        <li><b>Journeys built for you</b> — AI weaves lessons that evolve with every step you take.</li>
                        <li><b>Unlock your strengths</b> — Smart quizzes pinpoint where you shine and lift you higher.</li>
                        <li><b>Celebrate every win</b> — Earn <b>WSD Tokens</b> that prove your progress is real.</li>
                        <li><b>Grow with guidance</b> — Connect with mentors, human or AI, who light your way.</li>
                        <li><b>Own your learning</b> — Trade <b>tokens</b> for exclusive wisdom that’s yours to keep.</li>
                    </ul>

                    <div className="quiz-section">
                        <h3>Find Your Learning Superpower Now</h3>
                        <p className="section-lead">Three questions. A suggested style. The catalogue still waits behind a signed wallet login.</p>
                        <div className="quiz-form">
                            <label>
                                How do you prefer to learn new concepts?
                                <select
                                    value={quizAnswers.q1}
                                    onChange={(e) => setQuizAnswers({ ...quizAnswers, q1: e.target.value })}
                                >
                                    <option value="">Select...</option>
                                    <option value="visual">Through visuals (videos, diagrams)</option>
                                    <option value="text">Through reading and text</option>
                                    <option value="hands-on">Through practice and tasks</option>
                                </select>
                            </label>
                            <label>
                                How quickly do you like to progress?
                                <select
                                    value={quizAnswers.q2}
                                    onChange={(e) => setQuizAnswers({ ...quizAnswers, q2: e.target.value })}
                                >
                                    <option value="">Select...</option>
                                    <option value="fast">Quickly, key points only</option>
                                    <option value="moderate">Steady, balanced pace</option>
                                    <option value="slow">Slowly, with deep detail</option>
                                </select>
                            </label>
                            <label>
                                How important is feedback to you?
                                <select
                                    value={quizAnswers.q3}
                                    onChange={(e) => setQuizAnswers({ ...quizAnswers, q3: e.target.value })}
                                >
                                    <option value="">Select...</option>
                                    <option value="very">Very important</option>
                                    <option value="somewhat">Somewhat important</option>
                                    <option value="not">Not important</option>
                                </select>
                            </label>
                            <button type="button" onClick={handleQuizSubmit} className="quiz-submit-btn">
                                Get Your Style
                            </button>
                        </div>
                        {quizResult && <p className="quiz-result">{quizResult}</p>}
                    </div>
                </div>
            </section>

            <section id="approach" className="approach">
                <img
                    className="approach-media"
                    src="/pictures/wisdom-approach.jpg"
                    alt=""
                    width="1280"
                    height="720"
                />
                <div className="container">
                    <h2>AI That Knows You, Blockchain That Protects You</h2>
                    <p>Our AI bends to your unique rhythm, accelerating your growth—while blockchain locks in every triumph, forever yours.</p>
                </div>
            </section>

            <section id="contact" className="contact">
                <div className="container">
                    <h2>Step Into Your Future Now</h2>
                    <p>Ready to claim your place in the blockchain revolution? Join Wisdom Academy AI and start earning your legacy today!</p>
                    <p>Email: <a href="mailto:wisdomacademy@e-softworks.consulting">wisdomacademy@e-softworks.consulting</a></p>
                </div>
            </section>

            <section id="impressum" className="impressum">
                <div className="container">
                    <h2>Imprint</h2>
                    <p><strong>Website Operator:</strong><br />
                    Wisdom Academy<br />
                    A project by e-Softworks, Inc.<br />
                    Oliver Bodemer<br />
                    Sacramento, CA, USA</p>

                    <p><strong>Contact:</strong><br />
                    Email: <a href="mailto:info@wisdomacademy.ai">wisdomacademy@e-softworks.consulting</a><br />
                    Website: <a href="https://www.wisdomacademy.ai">https://www.wisdomacademy.ai</a></p>

                    <p><strong>Company Information:</strong><br />
                    e-Softworks, Inc.<br />
                    Registered in Sacramento, California, USA<br />
                    Legal Form: Corporation (Inc.)<br />
                    CEO: Oliver Bodemer</p>

                    <p><strong>Responsible for Content (§ 18 Abs. 2 MStV):</strong><br />
                    Oliver Bodemer<br />
                    Sacramento, CA, USA</p>

                    <p><strong>Disclaimer:</strong><br />
                    The content of this website has been created with great care. However, we do not guarantee the accuracy, completeness, or current validity of the content. As a service provider, we are responsible for our own content on these pages under general laws. We are not obligated to monitor transmitted or stored external information or investigate circumstances indicating illegal activity.</p>

                    <p><strong>Copyright:</strong><br />
                    All content and works published on this website are subject to copyright laws. Any reproduction, processing, distribution, or use beyond what copyright law allows requires prior written consent from the author.</p>
                </div>
            </section>

            <footer className="footer">
                <div className="container">
                    <p>&copy; 2026 Wisdom AI Learning Platform by e-Softworks, Inc. All rights reserved.</p>
                    <a href="#impressum">Imprint</a>
                </div>
            </footer>
        </div>
    );
};

export default WisdomAcademyStart;
